import { memo, useCallback, useState } from 'react';
import {
  IconBack,
  IconChevronDown,
  InformationalScreen,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { LocalDate } from '@js-joda/core';
import { BirthdayInput, FormContainer, Input, MainContainer, PhoneInput } from 'Components/utility';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';

type Props = {
  patient: Patient;
  onBack: (initialValues: Patient, currentValues: Patient) => void;
};

const AccountInfoAdult = ({ patient, onBack }: Props) => {
  const [values, setValues] = useState({
    ...patient,
  });

  const handleOnBack = useCallback(() => {
    onBack(patient, values);
  }, [patient, values, onBack]);

  const onUpdateEmail = useCallback(() => {
    mediator.publish('router:redirect', '/update-email');
  }, []);

  const onUpdatePhone = useCallback(() => {
    mediator.publish('router:redirect', '/update-phone');
  }, []);

  const handleDateChange = useCallback((date: LocalDate) => {
    // added as required for the input, this is disabled always
    setValues((prevValues) => ({
      ...prevValues,
      dateOfBirth: date.toString(),
    }));
  }, []);

  return (
    <MainContainer>
      <InformationalScreen
        topNavigationProps={{
          title: i18n.t('AccountInformationPersonalInfo.title'),
          buttonStart: {
            element: <IconBack testID="AccountInformationPersonalInfo.go-back" />,
            onPress: handleOnBack,
          },
          testID: 'AccountInformationPersonalInfo.title',
        }}
      >
        <InformationalScreen.Page>
          <InformationalScreen.Content>
            <Text marginTop={-8}>{i18n.t('AccountInformationPersonalInfo.content.subtitle')}</Text>
            <FormContainer>
              <Stack onPress={onUpdateEmail}>
                <Input
                  aria-label={i18n.t(
                    'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.emailAddress.label'
                  )}
                  label={i18n.t(
                    'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.emailAddress.label'
                  )}
                  testID="email"
                  inputMode="text"
                  value={values.email}
                  onFocus={onUpdateEmail}
                  inlineIconConfig={{
                    icon: (
                      <span style={{ transform: 'rotate(-90deg)' }}>
                        <IconChevronDown color="$interactive.core.enabled" />
                      </span>
                    ),
                    'aria-label': 'Change Email Address',
                    onPress: onUpdateEmail,
                  }}
                />
              </Stack>
              <Stack onPress={onUpdatePhone}>
                <PhoneInput
                  testID="AccountInformationPersonalInfo.accountInformationPersonalInfoForm.phone"
                  label={i18n.t(
                    'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.phone.label'
                  )}
                  placeholder={i18n.t(
                    'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.phone.placeholder'
                  )}
                  inputMode="text"
                  value={values.phone}
                  onFocus={onUpdatePhone}
                />
              </Stack>
              <Input
                aria-label={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.firstName.label'
                )}
                placeholder={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.firstName.placeholder'
                )}
                label={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.firstName.label'
                )}
                testID="firstName"
                value={values.firstName}
                disabled
              />
              <Input
                aria-label={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.lastName.label'
                )}
                placeholder={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.lastName.placeholder'
                )}
                label={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.lastName.label'
                )}
                testID="lastName"
                inputMode="text"
                value={values.lastName}
                disabled
              />
              <BirthdayInput
                testID="dateOfBirth"
                disabled
                initialDate={values.dateOfBirth ? LocalDate.parse(values.dateOfBirth) : undefined}
                onDateChange={handleDateChange}
              />
              <Input
                aria-label={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.country.label'
                )}
                label={i18n.t(
                  'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.country.label'
                )}
                testID="country"
                value={i18n.t(`Global.microcopy.countries.${values.country as 'US'}`)}
                disabled
              />
            </FormContainer>
          </InformationalScreen.Content>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default memo(AccountInfoAdult);
